// Personel › Tedavi Metrikleri — seçili dönemde açılan tedavilerin durumu,
// kontrol katılımı, en sık teşhis ve şikayetler (design-principles §4).
import React, { useEffect } from "react";
import { Box, Typography, Skeleton, Tooltip as MuiTooltip } from "@mui/material";
import {
  Healing as HealingIcon,
  EventAvailable as ControlIcon,
  ReportProblem as ComplaintIcon,
  DonutSmall as StatusIcon,
} from "@mui/icons-material";
import { useLazyGetPerformanceTreatmentsQuery } from "../../store/dashboardApi";
import { useClinicTheme } from "../../theme";
import { COLORS, TYPO, CARD_GAP } from "../../styles/uiConstants";
import KpiTile from "../../components/molecules/KpiTile";
import {
  AnalyticsCard,
  RankBarList,
  BodySkeleton,
  CardEmpty,
  nf,
  pct,
} from "../../components/organisms/charts/analyticsParts";
import { PerformanceTabFrame, usePreviewState, kpiGridSx } from "./performanceTabParts";
import useTerms from "../../hooks/useTerms";

/** Bileşim şeridi: parçalar toplamın yüzdesi kadar; ince ayraçlarla. */
function CompositionStrip({ items, total }) {
  return (
    <Box
      sx={{
        display: "flex",
        height: 12,
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: COLORS.slate100,
        gap: "2px",
      }}
    >
      {items
        .filter((it) => it.count > 0)
        .map((it) => (
          <MuiTooltip
            key={it.key}
            title={`${it.label}: ${nf(it.count)} · %${pct(it.count, total)}`}
            placement="top"
            arrow
          >
            <Box sx={{ width: `${(it.count / total) * 100}%`, bgcolor: it.color }} />
          </MuiTooltip>
        ))}
    </Box>
  );
}

/** Lejant satırı: renk anahtarı + etiket + sayı + yüzde (kimlik yalnız renge bırakılmaz). */
function LegendRow({ item, total, unit }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1.25, borderBottom: `1px solid ${COLORS.slate100}`, "&:last-of-type": { borderBottom: "none" } }}>
      <Box sx={{ width: 10, height: 10, borderRadius: 0.75, bgcolor: item.color, flexShrink: 0 }} />
      <Typography sx={{ flex: 1, fontSize: TYPO.body, color: COLORS.slate700 }}>{item.label}</Typography>
      <Typography
        sx={{
          fontSize: TYPO.body,
          fontWeight: 600,
          color: COLORS.slate800,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {nf(item.count)}
        {unit ? (
          <Box component="span" sx={{ fontWeight: 400, color: COLORS.slate500 }}>
            {` ${unit}`}
          </Box>
        ) : null}
      </Typography>
      <Typography
        sx={{
          width: 44,
          textAlign: "right",
          fontSize: TYPO.body,
          color: COLORS.slate500,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        %{pct(item.count, total)}
      </Typography>
    </Box>
  );
}

function CompositionSkeleton({ rows }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Skeleton width={120} height={40} />
      <Skeleton variant="rounded" height={12} />
      <Box>
        {[...Array(rows)].map((_, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1.25 }}>
            <Skeleton variant="rounded" width={10} height={10} />
            <Skeleton width={`${30 + ((i * 11) % 25)}%`} />
            <Box sx={{ flex: 1 }} />
            <Skeleton width={36} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** Büyük değer + açıklama (kart üst bölümü). */
function Headline({ value, caption }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: "1.75rem",
          fontWeight: 600,
          color: COLORS.slate800,
          lineHeight: 1.2,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500, mt: 0.25 }}>{caption}</Typography>
    </Box>
  );
}

export default function TreatmentMetricsTab({ token, period, onPeriodChange }) {
  const terms = useTerms();
  const [fetch, { data, isLoading, isFetching }] = useLazyGetPerformanceTreatmentsQuery();
  const { palette } = useClinicTheme();
  const preview = usePreviewState();

  useEffect(() => {
    if (token) fetch({ token, period });
  }, [token, period, fetch]);

  const loading = preview === "yukleniyor" || (!preview && (isLoading || isFetching || !data));
  const result = preview ? {} : data?.data || {};
  const summary = result.summary || {};
  const statusDist = result.statusDistribution || result.summary || {};
  const topDiagnoses = result.topDiagnoses || [];
  const topComplaints = result.topComplaints || [];
  const controlAttendance = result.controlAttendance || {};

  // Durum = anlam taşıyan renk (aktif tema rengi, tamamlandı yeşil, terk turuncu, iptal kırmızı)
  const statusItems = [
    { key: "active", label: "Aktif", color: palette.primary, count: statusDist.active ?? 0 },
    { key: "completed", label: "Tamamlandı", color: COLORS.success, count: statusDist.completed ?? 0 },
    { key: "abandoned", label: "Terk edildi", color: COLORS.warning, count: statusDist.abandoned ?? 0 },
    { key: "cancelled", label: "İptal", color: COLORS.error, count: statusDist.cancelled ?? 0 },
  ];
  const statusTotal = statusItems.reduce((s, it) => s + it.count, 0);
  const total = summary.total ?? statusTotal;
  const dropped = (statusDist.abandoned ?? 0) + (statusDist.cancelled ?? 0);

  const ctrlTotal = controlAttendance.total ?? 0;
  const ctrlAttended = controlAttendance.attended ?? 0;
  const controlItems = [
    { key: "attended", label: "Katıldı", color: palette.primary, count: ctrlAttended },
    { key: "missed", label: "Katılmadı", color: COLORS.slate300, count: Math.max(0, ctrlTotal - ctrlAttended) },
  ];

  return (
    <PerformanceTabFrame
      period={period}
      onPeriodChange={onPeriodChange}
      note="bu dönemde açılan tedaviler"
    >
      <Box sx={kpiGridSx}>
        <KpiTile
          loading={loading}
          label="Toplam tedavi"
          value={nf(total)}
          hint={`${nf(statusDist.active ?? 0)} tedavi devam ediyor`}
        />
        <KpiTile
          loading={loading}
          label="Tamamlanma"
          value={total ? `%${nf(summary.completionRate ?? 0)}` : "—"}
          hint={total ? `${nf(statusDist.completed ?? 0)} tedavi tamamlandı` : "Bu dönemde tedavi yok"}
        />
        <KpiTile
          loading={loading}
          label="Kontrol katılımı"
          value={ctrlTotal ? `%${nf(controlAttendance.rate ?? 0)}` : "—"}
          hint={ctrlTotal ? `${nf(ctrlAttended)} / ${nf(ctrlTotal)} kontrole gelindi` : "Planlı kontrol yok"}
        />
        <KpiTile
          loading={loading}
          label="Terk ve iptal"
          value={nf(dropped)}
          hint={total ? `Yarım kalma oranı %${pct(dropped, total)}` : "Bu dönemde tedavi yok"}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: CARD_GAP,
          gridTemplateColumns: { xs: "1fr", lg: "1.4fr 1fr" },
        }}
      >
        <AnalyticsCard
          title="Durum dağılımı"
          subtitle="Bu dönemde açılan tedavilerin güncel durumu"
          icon={<StatusIcon />}
          bodyHeight={360}
        >
          {loading ? (
            <CompositionSkeleton rows={4} />
          ) : !statusTotal ? (
            <CardEmpty
              icon={<StatusIcon />}
              title="Bu dönemde tedavi yok"
              description={`${terms.patients}a tedavi başlatıldıkça durumlara göre dağılım burada görünür.`}
            />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Headline value={nf(statusTotal)} caption="tedavi" />
              <CompositionStrip items={statusItems} total={statusTotal} />
              <Box>
                {statusItems.map((it) => (
                  <LegendRow key={it.key} item={it} total={statusTotal} />
                ))}
              </Box>
            </Box>
          )}
        </AnalyticsCard>

        <AnalyticsCard
          title="Kontrol katılımı"
          subtitle="Tedavi planındaki kontrol aylarına gelinme"
          icon={<ControlIcon />}
          bodyHeight={360}
        >
          {loading ? (
            <CompositionSkeleton rows={2} />
          ) : !ctrlTotal ? (
            <CardEmpty
              icon={<ControlIcon />}
              title="Planlı kontrol yok"
              description="Tedavilere kontrol ayı eklendikçe katılım oranı burada görünür."
            />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Headline value={`%${nf(controlAttendance.rate ?? 0)}`} caption={`${nf(ctrlTotal)} planlı kontrol`} />
              <CompositionStrip items={controlItems} total={ctrlTotal} />
              <Box>
                {controlItems.map((it) => (
                  <LegendRow key={it.key} item={it} total={ctrlTotal} unit="kontrol" />
                ))}
              </Box>
            </Box>
          )}
        </AnalyticsCard>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: CARD_GAP,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        <AnalyticsCard
          title="En sık teşhisler"
          subtitle="Bu dönemde açılan tedavilerde, tedavi sayısına göre"
          icon={<HealingIcon />}
          bodyHeight={320}
        >
          {loading ? (
            <BodySkeleton kind="list" />
          ) : (
            <RankBarList
              items={topDiagnoses.map((d) => ({ name: d.diagnosis || d.name || "—", count: d.count ?? 0 }))}
              color={palette.primary}
              total={total}
              unit="tedavi"
              emptyText={{
                icon: <HealingIcon />,
                title: "Teşhis girilmemiş",
                description: "Tedavilere teşhis eklendikçe en sık teşhisler burada sıralanır.",
              }}
            />
          )}
        </AnalyticsCard>

        <AnalyticsCard
          title="En sık şikayetler"
          subtitle="Bu dönemde açılan tedavilerde, tedavi sayısına göre"
          icon={<ComplaintIcon />}
          bodyHeight={320}
        >
          {loading ? (
            <BodySkeleton kind="list" />
          ) : (
            <RankBarList
              items={topComplaints.map((c) => ({ name: c.name || "Belirtilmedi", count: c.count ?? 0 }))}
              color={palette.primary}
              total={total}
              unit="tedavi"
              emptyText={{
                icon: <ComplaintIcon />,
                title: "Şikayet girilmemiş",
                description: `Tedavilere ${terms.patientLower} şikayeti eklendikçe en sık olanlar burada sıralanır.`,
              }}
            />
          )}
        </AnalyticsCard>
      </Box>
    </PerformanceTabFrame>
  );
}
